/**
 * Formats a given number into a properly formatted currency based on the provided currency code
 * e.g. 1000.00 USD => $1,000.00 or 1000.00 JPY => ¥1,000.00
 * @param number A string representation of the quantity of units of currency
 * @param currencyCode The three letter ISO 4217 code representing the currency we want to convert to
 * @returns A string formatted to display the given number in the given currency
 */
export const formatAsCurrency = (
    number: string | undefined,
    currencyCode: string | undefined,
): string => {
    if (!number || !currencyCode) {
        return "-";
    }
    return (+number).toLocaleString("en-US", {
        style: "currency",
        currency: currencyCode,
    });
};

/**
 * Removes any characters that don't match [a-zA-Z0-9] and replaces them with a "_" that can be safely used for keys
 * @param toConvert A source string to clean
 * @returns A cleaned string with risky characters removed
 */
export const removeWhitespace = (convertFrom?: string): string => {
    return convertFrom?.replace(/\W/g, "_") ?? "";
};

/**
 * Removes file extensions from string values by removing all values after the last `.` in the string and maintaining other instances of `.`.
 * Please note that this WILL FAIL for extensions that have a `.` in them such as `.tar.gz` as only `.gz` will be removed.
 * @param filename The filename we want to remove the extension from
 * @returns The filename without the extension or the original string if there was no extension
 */
export const removeFileExtension = (filename: string): string => {
    return (filename ?? "").replace(/\.[^/.]+$/, "");
};

export const getHashCode = (s: string): number =>
    s.split("").reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
    }, 0);
